import React, { useCallback, useEffect, useState } from 'react';
import { Skeleton, TextField } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  ICallScribeCallListItem,
  CallScribeTimeEntryStatus,
} from 'services/callScribe/types';
import { CallScribePipelineForCallSelector } from 'store/callScribe/selectors';
import { ConfigurationSelector } from 'store/configuration';
import { isContactScreen } from 'components/CallScribe/contactScreen';
import { ThunkGetCallScribeCall } from 'store/callScribe/thunks';
import { formatLocalDateTime } from 'components/CallScribe/callLogColumns';
import { customerNotesForServer, resolveSuggestedCustomerNotes } from 'components/CallScribe/reviewHelpers';
import { useTimeEntryOptions } from 'components/CallScribe/useTimeEntryOptions';
import {
  AUTO_POST_TITLE,
  CUSTOMER_NOTES_OFF,
  autoPostLine,
  NOTES_LOADING_LABEL,
  customerNotesState,
  fallbackDurationMinutes,
  postedDurationOf,
  isAwaitingAutomaticPost,
  isInFlightStage,
  type IReviewPhaseInput,
  showsManualSubmit,
  showsSubmitControl,
} from 'components/CallScribe/postCallReviewPhase';
import PostedTimeEntryEditor from 'components/CallScribe/PostedTimeEntryEditor';
import TimeEntrySection from 'components/CallScribe/TimeEntrySection';
import usePaperStyles from 'components/CallScribe/paperStyles';
import { useTimeEntrySubmit } from 'components/CallScribe/useTimeEntrySubmit';

export interface ITimeEntrySuggestionProps {
  call: ICallScribeCallListItem;
  reconcileExhausted?: boolean;
}

export const NO_TICKET_FOR_ENTRY =
  'This call was placed from a contact record, so there is no ticket to post a time entry to.';

export const NO_TICKET_LINKED = 'This call isn\'t linked to a ticket, so there is nowhere to post its time.';

export const CHECKING_AUTO_POST = 'Checking whether this entry posts on its own';

export const AUTO_POST_CHECK_FAILED =
  'We couldn\'t check whether this time entry posts to the ticket on its own, so it can\'t be submitted yet.';

const TimeEntrySuggestion: React.FC<ITimeEntrySuggestionProps> = ({ call, reconcileExhausted }) => {
  const paper = usePaperStyles();
  const dispatch = useAppDispatch();
  const live = useAppSelector(CallScribePipelineForCallSelector(call.id));
  const fromContactRecord = isContactScreen(useAppSelector(ConfigurationSelector));

  const alreadyPosted = call.linkStatus === CallScribeTimeEntryStatus.Posted;
  const alreadyRejected = call.linkStatus === CallScribeTimeEntryStatus.Rejected;
  const derived = fallbackDurationMinutes(call.suggestedDurationMinutes, call.durationSeconds);

  const seededDuration = derived > 0 ? derived : (call.hasIntelligence ? 0 : 1);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const durationValue = duration ?? seededDuration;
  const fields = useTimeEntryOptions(call.crmSourceId, call.id);
  const [customerNotes, setCustomerNotes] = useState<string | undefined>(undefined);
  const suggestedNotes = resolveSuggestedCustomerNotes(
    fields.options?.noteOverrides?.customerNotePrefill,
    call.summaryCustomerFacing,
  );
  const customerNotesEdited = customerNotes !== undefined && customerNotes !== suggestedNotes;
  const internalNotes = call.summaryInternal ?? '';
  const notesState = customerNotesState(fields.options, fields.error, call.crmSourceId);
  const notesPosted = notesState === 'on';

  const reread = useCallback(async (): Promise<ICallScribeCallListItem | null | undefined> => {
    const refreshed = await dispatch(ThunkGetCallScribeCall(call.id));
    return refreshed.payload as ICallScribeCallListItem | null | undefined;
  }, [dispatch, call.id]);
  const { submit, discard, error, posting, discarding } = useTimeEntrySubmit(call.id, reread);

  // A row that does not say whether the server posts this call itself must not offer Submit: on a
  // call the server is about to post, that click is a second entry (dev2 step 71, call 1189: the
  // ticket's call list carried null while the call itself said true). The single-call read always
  // answers, so ask it once and keep its answer until the row carries one of its own.
  const [checked, setChecked] = useState<{ callId: number; willPost: boolean | 'failed' } | null>(null);
  const [checkAttempt, setCheckAttempt] = useState(0);
  const checkedAnswer = checked?.callId === call.id ? checked.willPost : undefined;
  const willPostAutomatically = typeof call.willPostAutomatically === 'boolean'
    ? call.willPostAutomatically
    : typeof checkedAnswer === 'boolean' ? checkedAnswer : undefined;
  const willPostKnown = willPostAutomatically !== undefined;
  const mustCheck = !willPostKnown && !alreadyPosted && !alreadyRejected && Boolean(call.externalTicketId);
  useEffect(() => {
    if (!mustCheck) return undefined;
    let cancelled = false;
    const id = call.id;
    reread()
      .then(row => {
        if (cancelled) return;
        const answer = row?.willPostAutomatically;
        setChecked({ callId: id, willPost: typeof answer === 'boolean' ? answer : 'failed' });
      })
      .catch(() => {
        if (!cancelled) setChecked({ callId: id, willPost: 'failed' });
      });
    return () => {
      cancelled = true;
    };
    // checkAttempt re-runs the read after a failure.
  }, [mustCheck, reread, call.id, checkAttempt]);
  const autoPostCheckFailed = !willPostKnown && checkedAnswer === 'failed';

  const phaseInput: IReviewPhaseInput = {
    hasIntelligence: call.hasIntelligence,
    pipelineStage: live?.stage ?? call.pipelineStage,
    linkStatus: call.linkStatus,
    consentResult: call.consentResult,
    status: call.status,
    answeredAtUtc: call.answeredAtUtc,
    reconcileExhausted,
    willPostAutomatically,
    ticketless: !call.externalTicketId,
  };
  const technicianEditedTheEntry =
    customerNotesEdited || (duration !== undefined && duration !== seededDuration);
  const offersSubmit = willPostKnown && showsSubmitControl(phaseInput, technicianEditedTheEntry);
  const offersManualSubmit = showsManualSubmit(phaseInput);
  const awaitingAutomaticPost = isAwaitingAutomaticPost(phaseInput);

  const headingId = `cs-drawer-${call.id}-entry`;

  if (alreadyPosted) {
    return (
      <section className={paper.section} aria-labelledby={headingId}>
        <div className={paper.sectionHead}>
          <h3 id={headingId} className={paper.sectionLabel}>Time entry</h3>
        </div>
        <div className={`${paper.notice} ${paper.noticeGood}`}>
          <p className={paper.noticeTitle}>
            <CheckCircleRoundedIcon aria-hidden />
            Time entry posted{call.linkPostedAtUtc ? ` at ${formatLocalDateTime(call.linkPostedAtUtc)}` : ''}
          </p>
        </div>
        <PostedTimeEntryEditor
          key={call.id}
          callId={call.id}
          initialMinutes={postedDurationOf(call) ?? derived}
        />
      </section>
    );
  }

  if (alreadyRejected) {
    return <p className={paper.small}>Suggestion rejected — no time entry posted.</p>;
  }

  if (!call.hasIntelligence && isInFlightStage(live?.stage ?? call.pipelineStage)) {
    return (
      <section className={paper.section} aria-labelledby={headingId}>
        <div className={paper.sectionHead}>
          <h3 id={headingId} className={paper.sectionLabel}>Time entry</h3>
        </div>
        <p className={paper.small}>Generating summary… The time entry is ready to review once it is written.</p>
      </section>
    );
  }

  if (!call.externalTicketId) {
    return (
      <section className={paper.section} aria-labelledby={headingId}>
        <div className={paper.sectionHead}>
          <h3 id={headingId} className={paper.sectionLabel}>Time entry</h3>
        </div>
        <p className={paper.small}>{fromContactRecord ? NO_TICKET_FOR_ENTRY : NO_TICKET_LINKED}</p>
      </section>
    );
  }

  const clockCaveat = call.answeredAtUtc && call.endedAtUtc
    ? undefined
    : 'This call has no recorded answer time, so check the duration before submitting.';

  const onPost = (): void => {
    submit({
      durationMinutes: durationValue,
      ...(customerNotesEdited && customerNotes !== undefined
        ? { customerNotes: customerNotesForServer(customerNotes) }
        : {}),
      internalNotes,
      workTypeExternalId:
        fields.workType && fields.workType.length > 0 ? fields.workType : null,
      billingTypeExternalId:
        fields.billingType && fields.billingType.length > 0 ? fields.billingType : null,
    });
  };

  return (
    <TimeEntrySection
      idBase={`cs-drawer-${call.id}`}
      duration={durationValue}
      onDurationChange={setDuration}
      fields={fields}
      endedAtUtc={call.endedAtUtc}
      suggestedMinutes={call.suggestedDurationMinutes}
      caveat={clockCaveat}
    >
      {call.hasIntelligence ? null : (
        <p className={paper.caption}>
          No summary was written for this call, so the entry starts from the length of the call.
        </p>
      )}
      {notesPosted ? (
        <TextField
          className={paper.field}
          label="Customer-facing notes"
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          InputLabelProps={{ shrink: true }}
          value={customerNotes ?? suggestedNotes}
          onChange={e => {
            setCustomerNotes(e.target.value);
          }}
        />
      ) : notesState === 'loading' ? (
        <div className={paper.field} role="status" aria-label={NOTES_LOADING_LABEL}>
          <Skeleton variant="rounded" height={64} animation="pulse" />
        </div>
      ) : (
        <p className={paper.small}>{CUSTOMER_NOTES_OFF}</p>
      )}
      <div className={paper.sectionHead}>
        <span className={paper.sectionLabel}>
          Internal notes
          <span className={paper.readOnlyChip}>Read-only</span>
        </span>
      </div>
      <div
        className={paper.readOnlyBlock}
        role="region"
        aria-label="Internal notes, read-only"
        aria-readonly="true"
        tabIndex={0}
      >
        {internalNotes || 'No internal note was produced for this call.'}
      </div>
      {call.linkStatus === CallScribeTimeEntryStatus.Failed ? (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeTitle}>
            <ErrorOutlineRoundedIcon aria-hidden />
            The PSA rejected this time entry.
          </p>
          {call.linkErrorMessage ? (
            <p className={paper.noticeBody}>{call.linkErrorMessage}</p>
          ) : null}
          <p className={paper.noticeBody}>
            Nothing was posted. Adjust the entry and submit again.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeBody}>{error}</p>
        </div>
      ) : null}

      {!willPostKnown ? (
        autoPostCheckFailed ? (
          <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
            <p className={paper.noticeBody}>{AUTO_POST_CHECK_FAILED}</p>
            <div className={paper.actions}>
              <button
                type="button"
                className={paper.ghostButton}
                onClick={() => { setChecked(null); setCheckAttempt(n => n + 1); }}
              >
                Try again
              </button>
            </div>
          </div>
        ) : (
          <div className={paper.field} role="status" aria-label={CHECKING_AUTO_POST}>
            <Skeleton variant="rounded" height={36} animation="pulse" />
          </div>
        )
      ) : offersSubmit ? (
        <div className={paper.actions}>
          <button
            type="button"
            className={paper.primaryButton}
            onClick={onPost}
            disabled={posting || discarding || durationValue <= 0}
          >
            {posting ? 'Submitting…' : offersManualSubmit ? 'Submit time entry' : 'Post my version now'}
          </button>
          {offersManualSubmit ? (
            <button
              type="button"
              className={paper.ghostButton}
              onClick={discard}
              disabled={posting || discarding}
            >
              {discarding ? 'Discarding…' : 'Discard'}
            </button>
          ) : null}
        </div>
      ) : awaitingAutomaticPost ? (
        <div className={`${paper.notice} ${paper.noticeInfo}`} role="status">
          <p className={paper.noticeTitle}>
            <InfoOutlinedIcon aria-hidden />
            {AUTO_POST_TITLE}
          </p>
          <p className={paper.noticeBody}>{autoPostLine(notesPosted)}</p>
        </div>
      ) : null}
    </TimeEntrySection>
  );
};

export default TimeEntrySuggestion;
